import React, { useEffect, useRef, useState } from "react";
import { View } from "react-native";
import AppTheme from "@toothfairy/shared-ui/AppTheme";

const AudioWaveform = ({
  audioStream,
  isRecording,
  audioElement,
  isPlaying,
  barCount = 32,
  color,
  style,
}) => {
  const { currentTheme } = AppTheme.useTheme();
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const analyserRef = useRef(null);
  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const barColor = color || currentTheme?.theme?.primary;

  const isActive = isRecording || isPlaying;

  useEffect(() => {
    if (audioStream && isRecording && !isInitialized) {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const audioContext = new AudioContext();
        audioContextRef.current = audioContext;

        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;
        analyserRef.current = analyser;

        const source = audioContext.createMediaStreamSource(audioStream);
        source.connect(analyser);
        sourceRef.current = source;

        setIsInitialized(true);
      } catch (error) {
        console.error("Error initializing audio analyzer:", error);
      }
    }

    if (audioElement && isPlaying && !isInitialized && !isRecording) {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const audioContext = new AudioContext();
        audioContextRef.current = audioContext;

        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;
        analyserRef.current = analyser;

        const source = audioContext.createMediaElementSource(audioElement);
        source.connect(analyser);
        analyser.connect(audioContext.destination);
        sourceRef.current = source;

        setIsInitialized(true);
      } catch (error) {
        console.error("Error initializing audio analyzer for playback:", error);
      }
    }

    if (!isActive && isInitialized) {
      if (sourceRef.current) {
        try {
          sourceRef.current.disconnect();
        } catch (e) {}
        sourceRef.current = null;
      }
      if (audioContextRef.current && audioContextRef.current.state !== "closed") {
        try {
          audioContextRef.current.close();
        } catch (e) {}
        audioContextRef.current = null;
      }
      setIsInitialized(false);
    }

    return () => {
      if (sourceRef.current) {
        try {
          sourceRef.current.disconnect();
        } catch (e) {}
        sourceRef.current = null;
      }
      if (audioContextRef.current && audioContextRef.current.state !== "closed") {
        try {
          audioContextRef.current.close();
        } catch (e) {}
        audioContextRef.current = null;
      }
      setIsInitialized(false);
    };
  }, [audioStream, isRecording, audioElement, isPlaying, isActive]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const analyser = analyserRef.current;
    const bufferLength = analyser ? analyser.frequencyBinCount : 0;
    const dataArray = bufferLength > 0 ? new Uint8Array(bufferLength) : null;

    const drawIdleBars = () => {
      const barWidth = canvas.width / barCount;
      const gap = 2;
      const idleHeight = 4;

      for (let i = 0; i < barCount; i++) {
        const x = i * barWidth;
        const y = (canvas.height - idleHeight) / 2;

        ctx.fillStyle = barColor;
        ctx.globalAlpha = 0.3;
        ctx.beginPath();
        ctx.roundRect(x + gap / 2, y, barWidth - gap, idleHeight, 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    };

    const draw = () => {
      animationFrameRef.current = requestAnimationFrame(draw);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (!isActive || !isInitialized || !analyser || !dataArray) {
        drawIdleBars();
        return;
      }

      analyser.getByteFrequencyData(dataArray);

      const barWidth = canvas.width / barCount;
      const gap = 2;
      const step = Math.floor(bufferLength / barCount);

      for (let i = 0; i < barCount; i++) {
        const value = dataArray[i * step] || 0;
        const barHeight = (value / 255) * canvas.height * 0.9;
        const x = i * barWidth;
        const y = (canvas.height - barHeight) / 2;

        ctx.fillStyle = barColor;
        ctx.beginPath();
        ctx.roundRect(x + gap / 2, y, barWidth - gap, barHeight, 3);
        ctx.fill();
      }
    };

    draw();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isInitialized, isActive, barCount, barColor]);

  return (
    <View style={[{ alignItems: "center", justifyContent: "center" }, style]}>
      <canvas
        ref={canvasRef}
        width={280}
        height={60}
        style={{
          borderRadius: 8,
          backgroundColor: "transparent",
        }}
      />
    </View>
  );
};

export default AudioWaveform;